/**
 * Month calendar: 6×7 grid (Monday first). Phones/portrait: compact cells with coloured dots
 * (my RSVP). iPad landscape (≥ 1024 px): taller cells with time + title pills. Swipe sideways or
 * use the arrows to change month; "Í dag" jumps back.
 */
import { CalendarCheck, ChevronLeft, ChevronRight } from 'lucide-react';
import { useMemo, useRef, type PointerEvent } from 'react';
import type { EventDTO } from '../../../shared/dto/events';
import { useRequiredMe } from '../../auth/AuthProvider';
import { Button, cx, IconButton, Spinner } from '../../components/ui';
import { useI18n, useT, type MessageKey } from '../../i18n';
import { addMonths, capitalize, formatDay, formatMonthYear, formatTime, groupByDay, monthGrid, ymOf } from '../../lib/dates';
import { eventTone, TONE_PILL, ToneDot, type Tone } from './shared';

function useSwipe(onSwipe: (dir: 1 | -1) => void) {
  const start = useRef<{ x: number; y: number; id: number; at: number } | null>(null);
  const lastSwipe = useRef(0);
  return {
    handlers: {
      onPointerDown: (e: PointerEvent) => {
        if (e.pointerType === 'mouse') return;
        start.current = { x: e.clientX, y: e.clientY, id: e.pointerId, at: Date.now() };
      },
      onPointerUp: (e: PointerEvent) => {
        const s = start.current;
        start.current = null;
        if (!s || s.id !== e.pointerId) return;
        const dx = e.clientX - s.x;
        const dy = e.clientY - s.y;
        if (Math.abs(dx) > 50 && Math.abs(dx) > 1.5 * Math.abs(dy) && Date.now() - s.at < 900) {
          lastSwipe.current = Date.now();
          onSwipe(dx < 0 ? 1 : -1);
        }
      },
      onPointerCancel: () => {
        start.current = null;
      },
    },
    justSwiped: () => Date.now() - lastSwipe.current < 500,
  };
}

const WEEKDAY_KEYS: MessageKey[] = ['calendar.wd.1', 'calendar.wd.2', 'calendar.wd.3', 'calendar.wd.4', 'calendar.wd.5', 'calendar.wd.6', 'calendar.wd.7'];

export function MonthCalendar({
  ym,
  onYm,
  onToday,
  selected,
  onSelect,
  today,
  events,
  loading,
}: {
  ym: string;
  onYm: (ym: string) => void;
  onToday: () => void;
  selected: string | null;
  onSelect: (date: string | null) => void;
  today: string;
  events: EventDTO[] | undefined;
  loading: boolean;
}) {
  const t = useT();
  const { loc } = useI18n();
  const me = useRequiredMe();
  const weeks = useMemo(() => monthGrid(ym), [ym]);
  const byDay = useMemo(() => groupByDay(events ?? [], (e) => e.startsAt), [events]);
  const swipe = useSwipe((dir) => onYm(addMonths(ym, dir)));
  const monthTitle = capitalize(formatMonthYear(ym, loc), loc);

  return (
    <section aria-label={monthTitle} className="flex flex-col gap-3">
      <div className="flex items-center gap-1">
        <h2 className="flex min-w-0 flex-1 items-center gap-2 text-[22px] font-bold" aria-live="polite">
          <span className="truncate">{monthTitle}</span>
          {loading ? <Spinner className="[&_svg]:size-5" /> : null}
        </h2>
        <Button variant="secondary" size="sm" icon={CalendarCheck} onClick={onToday} className="min-h-11">
          {t('calendar.today')}
        </Button>
        <IconButton icon={ChevronLeft} label={t('calendar.prev_month')} onClick={() => onYm(addMonths(ym, -1))} />
        <IconButton icon={ChevronRight} label={t('calendar.next_month')} onClick={() => onYm(addMonths(ym, 1))} />
      </div>

      <div className="no-select rounded-2xl border border-[var(--line)] bg-[var(--surface)] p-1.5 lg:p-2" style={{ touchAction: 'pan-y' }} {...swipe.handlers}>
        <div className="grid grid-cols-7 pb-1" aria-hidden>
          {WEEKDAY_KEYS.map((k) => (
            <div key={k} className="py-1 text-center text-[13px] font-semibold tracking-wide text-[var(--muted)] uppercase">
              {t(k)}
            </div>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-0.5 lg:gap-1">
          {weeks.flat().map((date) => {
            const list = byDay.get(date) ?? [];
            const inMonth = ymOf(date) === ym;
            const isToday = date === today;
            const isSel = date === selected;
            const label = list.length > 0 ? `${formatDay(date, loc)}, ${t('calendar.day_events', { count: list.length })}` : formatDay(date, loc);
            return (
              <button
                key={date}
                type="button"
                aria-label={label}
                aria-pressed={isSel}
                aria-current={isToday ? 'date' : undefined}
                onClick={() => {
                  if (swipe.justSwiped()) return;
                  onSelect(isSel ? null : date);
                }}
                className={cx(
                  'flex min-h-12 min-w-0 flex-col items-center gap-1 rounded-xl px-0.5 pt-1 pb-1.5 transition lg:min-h-[96px] lg:items-stretch lg:px-1',
                  isSel ? 'bg-[var(--accent-soft)] ring-2 ring-[var(--accent)]' : 'active:bg-[var(--accent-soft)]',
                  !inMonth && 'opacity-45',
                )}
              >
                <span
                  className={cx(
                    'inline-flex size-8 shrink-0 items-center justify-center rounded-full text-[16px] tabular-nums lg:size-7 lg:self-start lg:text-[15px]',
                    isToday ? 'bg-[var(--accent)] font-bold text-[var(--accent-ink)]' : 'font-semibold',
                  )}
                >
                  {Number(date.slice(8))}
                </span>
                {list.length > 0 ? (
                  <>
                    <span className="flex items-center gap-0.5 lg:hidden">
                      {list.slice(0, 3).map((ev) => (
                        <ToneDot key={ev.id} tone={eventTone(ev, me.section)} />
                      ))}
                      {list.length > 3 ? <span className="text-[11px] leading-none font-bold text-[var(--muted)]">+</span> : null}
                    </span>
                    <span className="hidden w-full min-w-0 flex-col gap-0.5 lg:flex">
                      {list.slice(0, 3).map((ev) => (
                        <span
                          key={ev.id}
                          className={cx('block truncate rounded-md px-1 text-left text-[12px] leading-5 font-medium', TONE_PILL[eventTone(ev, me.section)])}
                        >
                          <span className="tabular-nums">{formatTime(ev.startsAt, loc)}</span> {ev.title}
                        </span>
                      ))}
                      {list.length > 3 ? <span className="px-1 text-left text-[12px] font-semibold text-[var(--muted)]">+{list.length - 3}</span> : null}
                    </span>
                  </>
                ) : null}
              </button>
            );
          })}
        </div>
      </div>
      <Legend />
    </section>
  );
}

const LEGEND: { tone: Tone; key: MessageKey }[] = [
  { tone: 'yes', key: 'calendar.rsvp.yes' },
  { tone: 'no', key: 'calendar.rsvp.no' },
  { tone: 'maybe', key: 'calendar.rsvp.maybe' },
  { tone: 'none', key: 'calendar.rsvp.none' },
  { tone: 'cancelled', key: 'calendar.event.cancelled' },
];

function Legend() {
  const t = useT();
  return (
    <ul className="flex flex-wrap items-center gap-x-4 gap-y-1 px-1 text-[14px] text-[var(--muted)]">
      {LEGEND.map((l) => (
        <li key={l.tone} className="inline-flex items-center gap-1.5">
          <ToneDot tone={l.tone} />
          {t(l.key)}
        </li>
      ))}
    </ul>
  );
}
